import React, { useState, useEffect } from "react";
import { useGlobal } from "../../global/GlobalContext";
import { useLocation, useNavigate } from "react-router-dom";
import { TextField, MenuItem, Select, FormControl, InputLabel } from "@mui/material";
import axios from "axios";
import {
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiMapPin,
  FiHome,
  FiBriefcase,
  FiX,
} from "react-icons/fi";

const EMPTY_ADDRESS = {
  address_label: "",
  full_name: "",
  email: "",
  mobile_number: "",
  alt_mob_number: "",
  address_line_1: "",
  address_line_2: "",
  city: "",
  state: "",
  zip_code: "",
  country: "",
};

const LABEL_ICON = {
  home: FiHome,
  office: FiBriefcase,
  others: FiMapPin,
};

const ShippingAddress = () => {
  const [shippingAddresses, setShippingAddresses] = useState([]);
  const [addressesLoading, setAddressesLoading] = useState(true);
  const { token } = useGlobal();
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [addressToCancel, setAddressToCancel] = useState(null);

  // Arriving from Checkout ("Add/Edit address" links) carries this state and
  // must land straight on the form, then bounce back to /checkout on save —
  // that hand-off is load-bearing, don't change it.
  const cameFromCheckout = location.state?.status === "edit" || location.state?.status === "add";
  const [showForm, setShowForm] = useState(cameFromCheckout);
  const [newAddress, setNewAddress] = useState(
    location.state?.address ? location.state.address : EMPTY_ADDRESS
  );
  const isEditing = !!newAddress.id;

  useEffect(() => {
    if (location.state?.address) {
      setNewAddress(location.state.address);
      setShowForm(true);
    }
  }, [location.state]);

  useEffect(() => {
    const fetchShipping = async () => {
      setAddressesLoading(true);
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/v1/get_customer_shipping_addresses`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const data = response.data;
        if (data.status) {
          setShippingAddresses(data.shipping_addresses);
        }
      } catch (error) {
        console.error("Error fetching shipping:", error);
      } finally {
        setAddressesLoading(false);
      }
    };
    fetchShipping();
  }, [token]);

  const handleSaveAddress = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const url = isEditing
        ? `${process.env.REACT_APP_API_URL}/api/v1/update_customer_shipping_addresses?address_id=${newAddress.id}`
        : `${process.env.REACT_APP_API_URL}/api/v1/add_customer_shipping_addresses`;

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(newAddress),
      });

      if (!response.ok) throw new Error(isEditing ? "Failed to update address" : "Failed to add address");

      const data = await response.json();

      if (isEditing) {
        setShippingAddresses((prev) =>
          prev.map((addr) => (addr.id === newAddress.id ? data.updated_address : addr))
        );
      } else {
        setShippingAddresses((prev) => [...prev, data.new_address]);
      }

      if (cameFromCheckout) {
        navigate(`../../${location.state.slug}`);
      } else {
        setShowForm(false);
        setNewAddress(EMPTY_ADDRESS);
      }
    } catch (error) {
      console.error("Error saving address:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAddress = async () => {
    if (!addressToCancel) return;
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/delete_customer_shipping_addresses/${addressToCancel}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) throw new Error("Failed to delete address");
      setShippingAddresses((prev) => prev.filter((address) => address.id !== addressToCancel));
    } catch (error) {
      console.error("Error deleting address:", error);
    } finally {
      setLoading(false);
      setShowWarning(false);
    }
  };

  const openAddForm = () => {
    setNewAddress(EMPTY_ADDRESS);
    setShowForm(true);
  };

  const openEditForm = (address) => {
    setNewAddress(address);
    setShowForm(true);
  };

  const closeForm = () => {
    if (cameFromCheckout) {
      navigate(`/${location.state.slug}`);
      return;
    }
    setShowForm(false);
    setNewAddress(EMPTY_ADDRESS);
  };

  if (showForm) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 md:p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-semibold text-gray-900">{isEditing ? "Edit Address" : "Add New Address"}</h2>
            <button
              onClick={closeForm}
              className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 text-gray-500"
              aria-label="Close"
            >
              <FiX size={16} />
            </button>
          </div>

          <form onSubmit={handleSaveAddress} className="space-y-4">
            <FormControl fullWidth size="small">
              <InputLabel sx={{ background: 'white', px: 0.5 }}>Type of Address</InputLabel>
              <Select
                value={newAddress.address_label}
                onChange={(e) => setNewAddress({ ...newAddress, address_label: e.target.value })}
                required
              >
                <MenuItem value="home">Home</MenuItem>
                <MenuItem value="office">Office</MenuItem>
                <MenuItem value="others">Others</MenuItem>
              </Select>
            </FormControl>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <TextField
                label="Full Name"
                fullWidth
                size="small"
                value={newAddress.full_name}
                onChange={(e) => setNewAddress({ ...newAddress, full_name: e.target.value })}
                required
              />
              <TextField
                label="Email"
                type="email"
                fullWidth
                size="small"
                value={newAddress.email}
                onChange={(e) => setNewAddress({ ...newAddress, email: e.target.value })}
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <TextField
                label="Mobile Number"
                fullWidth
                size="small"
                value={newAddress.mobile_number}
                onChange={(e) => setNewAddress({ ...newAddress, mobile_number: e.target.value })}
                required
              />
              <TextField
                label="Alt Mobile Number (Optional)"
                fullWidth
                size="small"
                value={newAddress.alt_mob_number}
                onChange={(e) => setNewAddress({ ...newAddress, alt_mob_number: e.target.value })}
              />
            </div>

            <TextField
              label="Address Line 1"
              multiline
              fullWidth
              size="small"
              value={newAddress.address_line_1}
              onChange={(e) => setNewAddress({ ...newAddress, address_line_1: e.target.value })}
              required
            />

            <TextField
              label="Address Line 2 (Optional)"
              fullWidth
              size="small"
              value={newAddress.address_line_2 || ''}
              onChange={(e) => setNewAddress({ ...newAddress, address_line_2: e.target.value })}
            />

            <div className="grid grid-cols-2 gap-4">
              <TextField
                label="City"
                fullWidth
                size="small"
                value={newAddress.city}
                onChange={(e) => setNewAddress({ ...newAddress, city: e.target.value })}
              />
              <TextField
                label="State"
                fullWidth
                size="small"
                value={newAddress.state}
                onChange={(e) => setNewAddress({ ...newAddress, state: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <TextField
                label="Zip Code"
                fullWidth
                size="small"
                value={newAddress.zip_code}
                onChange={(e) => setNewAddress({ ...newAddress, zip_code: e.target.value })}
              />
              <TextField
                label="Country"
                fullWidth
                size="small"
                value={newAddress.country}
                onChange={(e) => setNewAddress({ ...newAddress, country: e.target.value })}
              />
            </div>

            <div className="flex gap-3 pt-2">
              <button
                type="submit"
                disabled={loading}
                className="flex-1 bg-brand-600 text-white py-2.5 rounded-xl font-medium disabled:opacity-60"
              >
                {loading ? (
                  <span className="inline-flex items-center justify-center gap-2">
                    <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                    Saving...
                  </span>
                ) : isEditing ? "Update Address" : "Save Address"}
              </button>
              <button
                type="button"
                onClick={closeForm}
                className="px-5 py-2.5 rounded-xl font-medium bg-gray-100 text-gray-700"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold text-gray-900 hidden md:block">My Addresses</h2>
          <button
            onClick={openAddForm}
            className="ml-auto inline-flex items-center gap-1.5 bg-brand-600 text-white text-sm font-medium px-4 py-2 rounded-xl"
          >
            <FiPlus size={16} /> Add New Address
          </button>
        </div>

        {addressesLoading ? (
          <div className="space-y-4">
            {[...Array(2)].map((_, i) => (
              <div key={i} className="h-40 bg-gray-100 rounded-2xl animate-pulse" />
            ))}
          </div>
        ) : shippingAddresses.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-10 text-center flex flex-col items-center gap-3">
            <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center">
              <FiMapPin size={26} className="text-gray-400" />
            </div>
            <h3 className="font-semibold text-gray-900">No saved addresses yet</h3>
            <p className="text-sm text-gray-500 max-w-xs">
              Add a delivery address so checkout is faster next time.
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {shippingAddresses.filter(Boolean).map((ship) => {
              const LabelIcon = LABEL_ICON[ship.address_label] || FiMapPin;
              return (
                <div key={ship.id} className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className="w-8 h-8 rounded-full bg-brand-600/10 text-brand-600 flex items-center justify-center shrink-0">
                        <LabelIcon size={15} />
                      </span>
                      <span className="text-xs font-semibold text-gray-600 capitalize">{ship.address_label}</span>
                      {ship.is_default ? (
                        <span className="text-[11px] font-semibold text-green-700 bg-green-50 px-2 py-0.5 rounded-full">
                          Default
                        </span>
                      ) : null}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        onClick={() => openEditForm(ship)}
                        className="w-8 h-8 flex items-center justify-center rounded-full text-gray-500 hover:bg-gray-100"
                        aria-label="Edit address"
                      >
                        <FiEdit2 size={14} />
                      </button>
                      <button
                        onClick={() => { setShowWarning(true); setAddressToCancel(ship.id); }}
                        className="w-8 h-8 flex items-center justify-center rounded-full text-red-500 hover:bg-red-50"
                        aria-label="Delete address"
                      >
                        <FiTrash2 size={14} />
                      </button>
                    </div>
                  </div>

                  <div className="mt-3">
                    <p className="font-semibold text-gray-900">{ship.full_name}</p>
                    <div className="text-sm text-gray-600 mt-1 leading-relaxed">
                      <p>{ship.address_line_1}</p>
                      {ship.address_line_2 && <p>{ship.address_line_2}</p>}
                      <p>{ship.city}, {ship.state} - {ship.zip_code}</p>
                      {ship.country && <p>{ship.country}</p>}
                    </div>
                  </div>

                  <div className="mt-3 pt-3 border-t border-gray-100 text-xs text-gray-500 space-y-0.5">
                    <p>Email: <span className="text-gray-700">{ship.email}</span></p>
                    <p>Mobile: <span className="text-gray-700">{ship.mobile_number}</span></p>
                    {ship.alt_mob_number && (
                      <p>Alternate Mobile: <span className="text-gray-700">{ship.alt_mob_number}</span></p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showWarning && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
          <div className="bg-white p-6 rounded-2xl shadow-lg max-w-sm w-full">
            <p className="text-sm text-gray-600">
              Your shipping address will be deleted permanently.
            </p>
            <div className="mt-5 flex justify-end gap-3">
              <button onClick={() => setShowWarning(false)} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-xl text-sm font-medium">
                No
              </button>
              <button
                onClick={handleDeleteAddress}
                className="px-4 py-2 bg-brand-600 text-white rounded-xl text-sm font-medium disabled:opacity-60"
                disabled={loading}
              >
                {loading ? 'Removing...' : 'Yes, Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ShippingAddress;
